import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import axios from "axios";
import { Icon } from "./Icon";
import TriggerTypeHelp from "./TriggerTypeHelp";
import { normalizeTriggerConfig } from "./triggerConfig";

interface BotTrigger {
  id: string;
  name: string;
  triggerType:
    | "keyword"
    | "pattern"
    | "silence"
    | "activity_spike"
    | "time_based"
    | "user_join"
    | "raid"
    | "follow"
    | "subscribe"
    | "parameterized_keyword";
  enabled: boolean;
  config: {
    keywords?: string[];
    caseSensitive?: boolean;
    exactMatch?: boolean;
    pattern?: string;
    flags?: string;
    silenceThresholdMinutes?: number;
    messageThreshold?: number;
    timeWindowMinutes?: number;
    cronExpression?: string;
    timezone?: string;
    userWhitelist?: string[];
    userBlacklist?: string[];
    cooldownMinutes?: number;
    maxTriggersPerHour?: number;
    // Parameterized keyword config
    keyword?: string;
    actionType?: "message" | "webhook";
    webhookUrl?: string;
    allowedUsers?: "everyone" | "mods_only";
  };
  responses: {
    messages: string[];
    useRandom: boolean;
    includeContext?: boolean;
    personality?: string;
  };
  metadata?: {
    lastTriggered?: string;
    triggerCount?: number;
    cooldownUntil?: string;
  };
  activationCount?: number;
  createdAt: string;
  updatedAt: string;
}

interface TriggerManagerProps {
  authToken: string;
  userType?: "user" | "premium" | "vip" | "admin";
}

const TriggerManager: React.FC<TriggerManagerProps> = ({
  authToken,
  userType = "user",
}) => {
  const [triggers, setTriggers] = useState<BotTrigger[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedTriggerType, setSelectedTriggerType] = useState<string>("all");

  // Determine trigger limits based on user type
  const isPremium =
    userType === "premium" || userType === "vip" || userType === "admin";
  const maxTriggers = isPremium ? 100 : 5;
  const location = useLocation();
  const navigate = useNavigate();
  const [newTrigger, setNewTrigger] = useState<{
    name: string;
    triggerType: BotTrigger["triggerType"];
    config: BotTrigger["config"];
    responses: BotTrigger["responses"];
  }>({
    name: "",
    triggerType: "keyword",
    config: {
      keywords: [],
      caseSensitive: false,
      exactMatch: false,
      silenceThresholdMinutes: 5,
      cooldownMinutes: 1,
      maxTriggersPerHour: 10,
      messageThreshold: 50,
      timeWindowMinutes: 1,
      cronExpression: "0 * * * *",
      timezone: "America/New_York",
    },
    responses: {
      messages: [""],
      useRandom: false,
      includeContext: false,
    },
  });

  useEffect(() => {
    fetchTriggers();
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const fetchTriggers = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/bot/triggers", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setTriggers(response.data);
    } catch (error) {
      console.error("Error fetching triggers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrigger = async () => {
    if (!newTrigger.name || newTrigger.responses.messages[0] === "") return;

    // Check if user has reached their trigger limit
    if (triggers.length >= maxTriggers) {
      if (isPremium) {
        alert(`You have reached the maximum limit of ${maxTriggers} triggers.`);
      } else {
        alert(
          `Free users can only create up to ${maxTriggers} triggers. Upgrade to Premium for up to 100 triggers!`,
        );
      }
      return;
    }

    try {
      await axios.post(
        "/api/bot/triggers",
        {
          ...newTrigger,
          config: normalizeTriggerConfig(newTrigger.config),
          enabled: true,
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      // Reset form
      setNewTrigger({
        name: "",
        triggerType: "keyword",
        config: {
          keywords: [],
          caseSensitive: false,
          exactMatch: false,
          silenceThresholdMinutes: 5,
          cooldownMinutes: 1,
          maxTriggersPerHour: 10,
          messageThreshold: 50,
          timeWindowMinutes: 1,
          cronExpression: "0 * * * *",
          timezone: "America/New_York",
        },
        responses: {
          messages: [""],
          useRandom: false,
          includeContext: false,
        },
      });
      fetchTriggers();
      navigate("/dashboard/triggers");
    } catch (error) {
      console.error("Error creating trigger:", error);
      alert("Failed to create trigger");
    }
  };

  const handleUpdateTrigger = async (
    triggerId: string,
    updates: Partial<BotTrigger>,
  ) => {
    try {
      await axios.put(`/api/bot/triggers/${triggerId}`, updates, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      fetchTriggers();
    } catch (error) {
      console.error("Error updating trigger:", error);
      alert("Failed to update trigger");
    }
  };

  const handleDeleteTrigger = async (
    triggerId: string,
    triggerName: string,
  ) => {
    if (!window.confirm(`Delete trigger "${triggerName}"?`)) return;

    try {
      await axios.delete(`/api/bot/triggers/${triggerId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      fetchTriggers();
    } catch (error) {
      console.error("Error deleting trigger:", error);
      alert("Failed to delete trigger");
    }
  };

  const handleEditTrigger = (trigger: BotTrigger) => {
    setEditingTrigger(trigger.id);
    setEditFormData({
      name: trigger.name,
      triggerType: trigger.triggerType,
      config: { ...trigger.config },
      responses: { ...trigger.responses },
    });
  };

  const handleSaveEdit = async (triggerId: string) => {
    if (!editFormData || !editFormData.name) return;

    try {
      await axios.put(
        `/api/bot/triggers/${triggerId}`,
        {
          ...editFormData,
          config: normalizeTriggerConfig(editFormData.config),
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      setEditingTrigger(null);
      setEditFormData(null);
      fetchTriggers();
    } catch (error) {
      console.error("Error saving trigger:", error);
      alert("Failed to save trigger");
    }
  };

  const handleCancelEdit = () => {
    setEditingTrigger(null);
    setEditFormData(null);
  };

  const triggerTypeLabels = {
    keyword: "Keyword Match",
    pattern: "Pattern (Regex)",
    parameterized_keyword: "Command with Parameter",
    silence: "Silence Detection",
    activity_spike: "Activity Spike",
    time_based: "Time-based",
    user_join: "User Join",
    raid: "Raid",
    follow: "New Follower",
    subscribe: "New Subscriber",
  };

  const getCooldownStatus = (trigger: BotTrigger) => {
    if (!trigger.metadata?.cooldownUntil) return null;

    const cooldownEnd = new Date(trigger.metadata.cooldownUntil);
    const now = new Date();

    if (cooldownEnd <= now) return null;

    const remainingMs = cooldownEnd.getTime() - now.getTime();
    const remainingMinutes = Math.floor(remainingMs / 60000);
    const remainingSeconds = Math.floor((remainingMs % 60000) / 1000);

    if (remainingMinutes > 0) {
      return `${remainingMinutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const getRelativeTimeString = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) {
      return "just now";
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const renderTriggerConfig = (
    trigger: {
      triggerType: BotTrigger["triggerType"];
      config: BotTrigger["config"];
    },
    setTrigger: (value: any) => void,
    isEditing = false,
  ) => {
    switch (trigger.triggerType) {
      case "keyword":
        return (
          <>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                Keywords (comma-separated)
              </label>
              <input
                type="text"
                value={trigger.config.keywords?.join(", ") || ""}
                onChange={(e) =>
                  setTrigger({
                    ...trigger,
                    config: {
                      ...trigger.config,
                      keywords: e.target.value
                        .split(",")
                        .map((k) => k.trim())
                        .filter((k) => k),
                    },
                  })
                }
                placeholder="hello, hi, hey"
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "20px",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                <input
                  type="checkbox"
                  checked={trigger.config.caseSensitive}
                  onChange={(e) =>
                    setTrigger({
                      ...trigger,
                      config: {
                        ...trigger.config,
                        caseSensitive: e.target.checked,
                      },
                    })
                  }
                />
                Case Sensitive
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                <input
                  type="checkbox"
                  checked={trigger.config.exactMatch}
                  onChange={(e) =>
                    setTrigger({
                      ...trigger,
                      config: {
                        ...trigger.config,
                        exactMatch: e.target.checked,
                      },
                    })
                  }
                />
                Exact Match
              </label>
            </div>
          </>
        );

      case "pattern":
        return (
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                color: "#cbd5e0",
                fontSize: "14px",
              }}
            >
              Regular Expression Pattern
            </label>
            <input
              type="text"
              value={trigger.config.pattern || ""}
              onChange={(e) =>
                setTrigger({
                  ...trigger,
                  config: { ...trigger.config, pattern: e.target.value },
                })
              }
              placeholder="^!(help|commands)$"
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #4a5568",
                borderRadius: "6px",
                backgroundColor: "#1a202c",
                color: "#e2e8f0",
                fontFamily: "monospace",
                boxSizing: "border-box",
              }}
            />
          </div>
        );

      case "parameterized_keyword": {
        const radioNameSuffix = isEditing ? "_edit" : "";
        return (
          <>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                Command Keyword
              </label>
              <input
                type="text"
                value={trigger.config.keyword || ""}
                onChange={(e) =>
                  setTrigger({
                    ...trigger,
                    config: { ...trigger.config, keyword: e.target.value },
                  })
                }
                placeholder="!shoutout"
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  boxSizing: "border-box",
                }}
              />
              <div
                style={{ fontSize: "12px", color: "#a0aec0", marginTop: "4px" }}
              >
                A message starting with this command word (followed by a space
                or nothing) will trigger. Everything after becomes{" "}
                {"{{parameter}}"}
              </div>
            </div>

            <div style={{ marginTop: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                Action Type
              </label>
              <div style={{ display: "flex", gap: "20px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#cbd5e0",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name={`actionType${radioNameSuffix}`}
                    value="message"
                    checked={trigger.config.actionType !== "webhook"}
                    onChange={() =>
                      setTrigger({
                        ...trigger,
                        config: { ...trigger.config, actionType: "message" },
                      })
                    }
                  />
                  Send Message
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#cbd5e0",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name={`actionType${radioNameSuffix}`}
                    value="webhook"
                    checked={trigger.config.actionType === "webhook"}
                    onChange={() =>
                      setTrigger({
                        ...trigger,
                        config: { ...trigger.config, actionType: "webhook" },
                      })
                    }
                  />
                  Call Webhook URL
                </label>
              </div>
            </div>

            {trigger.config.actionType === "webhook" && (
              <div style={{ marginTop: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    color: "#cbd5e0",
                    fontSize: "14px",
                  }}
                >
                  Webhook URL
                </label>
                <input
                  type="text"
                  value={trigger.config.webhookUrl || ""}
                  onChange={(e) =>
                    setTrigger({
                      ...trigger,
                      config: {
                        ...trigger.config,
                        webhookUrl: e.target.value,
                      },
                    })
                  }
                  placeholder="https://example.com/api?user={{parameter}}"
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #4a5568",
                    borderRadius: "6px",
                    backgroundColor: "#1a202c",
                    color: "#e2e8f0",
                    fontFamily: "monospace",
                    boxSizing: "border-box",
                  }}
                />
                <div
                  style={{
                    fontSize: "12px",
                    color: "#a0aec0",
                    marginTop: "4px",
                  }}
                >
                  Use {"{{parameter}}"} in URL to include the captured text
                </div>
              </div>
            )}

            <div style={{ marginTop: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                Who Can Activate
              </label>
              <div style={{ display: "flex", gap: "20px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#cbd5e0",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name={`allowedUsers${radioNameSuffix}`}
                    value="everyone"
                    checked={trigger.config.allowedUsers !== "mods_only"}
                    onChange={() =>
                      setTrigger({
                        ...trigger,
                        config: {
                          ...trigger.config,
                          allowedUsers: "everyone",
                        },
                      })
                    }
                  />
                  Everyone
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#cbd5e0",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name={`allowedUsers${radioNameSuffix}`}
                    value="mods_only"
                    checked={trigger.config.allowedUsers === "mods_only"}
                    onChange={() =>
                      setTrigger({
                        ...trigger,
                        config: {
                          ...trigger.config,
                          allowedUsers: "mods_only",
                        },
                      })
                    }
                  />
                  Mods Only
                </label>
              </div>
            </div>

            {trigger.config.actionType !== "webhook" && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "12px",
                  backgroundColor: "#1a202c",
                  borderRadius: "6px",
                  border: "1px solid #4a5568",
                }}
              >
                <p style={{ color: "#a0aec0", fontSize: "12px", margin: 0 }}>
                  <strong style={{ color: "#9146FF" }}>Tip:</strong> Use{" "}
                  {"{{parameter}}"} in your response message to include the
                  captured text.
                  <br />
                  <br />
                  Example: If keyword is &quot;!so&quot; and someone types
                  &quot;!so neonvoid&quot;, the parameter &quot;neonvoid&quot;
                  will replace {"{{parameter}}"} in your response.
                </p>
              </div>
            )}
          </>
        );
      }

      case "silence":
        return (
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                color: "#cbd5e0",
                fontSize: "14px",
              }}
            >
              Minutes of Silence Before Triggering
            </label>
            <input
              type="number"
              value={newTrigger.config.silenceThresholdMinutes || 5}
              onChange={(e) =>
                setNewTrigger({
                  ...newTrigger,
                  config: {
                    ...newTrigger.config,
                    silenceThresholdMinutes: parseInt(e.target.value),
                  },
                })
              }
              min="1"
              style={{
                width: "100px",
                padding: "8px 12px",
                border: "1px solid #4a5568",
                borderRadius: "6px",
                backgroundColor: "#1a202c",
                color: "#e2e8f0",
                boxSizing: "border-box",
              }}
            />
          </div>
        );

      case "activity_spike":
        return (
          <>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                Message Threshold (messages per time window)
              </label>
              <input
                type="number"
                value={newTrigger.config.messageThreshold || 50}
                onChange={(e) =>
                  setNewTrigger({
                    ...newTrigger,
                    config: {
                      ...newTrigger.config,
                      messageThreshold: parseInt(e.target.value),
                    },
                  })
                }
                min="1"
                placeholder="50"
                style={{
                  width: "150px",
                  padding: "8px 12px",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                  marginTop: "12px",
                }}
              >
                Time Window (minutes)
              </label>
              <input
                type="number"
                value={newTrigger.config.timeWindowMinutes || 1}
                onChange={(e) =>
                  setNewTrigger({
                    ...newTrigger,
                    config: {
                      ...newTrigger.config,
                      timeWindowMinutes: parseInt(e.target.value),
                    },
                  })
                }
                min="1"
                placeholder="1"
                style={{
                  width: "100px",
                  padding: "8px 12px",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </>
        );

      case "time_based":
        return (
          <>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                Cron Expression
              </label>
              <input
                type="text"
                value={newTrigger.config.cronExpression || "0 * * * *"}
                onChange={(e) =>
                  setNewTrigger({
                    ...newTrigger,
                    config: {
                      ...newTrigger.config,
                      cronExpression: e.target.value,
                    },
                  })
                }
                placeholder="0 * * * * (every hour)"
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  fontFamily: "monospace",
                  boxSizing: "border-box",
                }}
              />
              <div
                style={{ fontSize: "12px", color: "#a0aec0", marginTop: "4px" }}
              >
                Examples: &quot;0 * * * *&quot; = hourly, &quot;0 0 * * *&quot;
                = daily, &quot;*/30 * * * *&quot; = every 30 min
              </div>
            </div>
            <div style={{ marginTop: "12px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                Timezone
              </label>
              <input
                type="text"
                value={newTrigger.config.timezone || "America/New_York"}
                onChange={(e) =>
                  setNewTrigger({
                    ...newTrigger,
                    config: { ...newTrigger.config, timezone: e.target.value },
                  })
                }
                placeholder="America/New_York"
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </>
        );

      case "user_join":
      case "raid":
      case "follow":
      case "subscribe":
        return (
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                color: "#cbd5e0",
                fontSize: "14px",
              }}
            >
              Event Configuration
            </label>
            <p style={{ fontSize: "13px", color: "#a0aec0", margin: "8px 0" }}>
              This trigger will automatically activate when a{" "}
              {trigger.triggerType.replace("_", " ")} event occurs. No
              additional configuration needed.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  // Remove loading screen to prevent flash

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "center",
        gap: "20px",
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0 20px",
      }}
    >
      <div
        style={{
          flex: "1",
          maxWidth: isMobile ? "100%" : "800px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? "16px" : "0",
            marginBottom: "24px",
          }}
        >
          <h3 style={{ fontSize: "20px", margin: 0, color: "#e2e8f0" }}>
            Response Triggers
          </h3>
          {location.pathname.includes("/add") ? (
            <Link
              to="/dashboard/triggers"
              style={{
                padding: "8px 16px",
                backgroundColor: "#6B7280",
                color: "white",
                border: "none",
                borderRadius: "6px",
                textDecoration: "none",
                fontSize: "14px",
                display: "inline-block",
              }}
            >
              Cancel
            </Link>
          ) : (
            <Link
              to="/dashboard/triggers/add"
              style={{
                padding: "8px 16px",
                backgroundColor: "#9146FF",
                color: "white",
                border: "none",
                borderRadius: "6px",
                textDecoration: "none",
                fontSize: "14px",
                display: "inline-block",
              }}
            >
              Add New Trigger
            </Link>
          )}
        </div>

        <Routes>
          <Route
            path="/"
            element={
              <>
                {/* Trigger Type Filter and Counter */}
                <div
                  style={{
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <label
                      style={{
                        color: "#cbd5e0",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Filter by type:
                    </label>
                    <div style={{ position: "relative" }}>
                      <select
                        value={selectedTriggerType}
                        onChange={(e) => setSelectedTriggerType(e.target.value)}
                        style={{
                          padding: "8px 32px 8px 12px",
                          border: "1px solid #4a5568",
                          borderRadius: "6px",
                          backgroundColor: "#1a202c",
                          color: "#e2e8f0",
                          appearance: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                          fontSize: "14px",
                          minWidth: "180px",
                          cursor: "pointer",
                        }}
                      >
                        <option value="all">All Triggers</option>
                        {Object.entries(triggerTypeLabels).map(
                          ([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ),
                        )}
                      </select>
                      <div
                        style={{
                          position: "absolute",
                          right: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          color: "#cbd5e0",
                          fontSize: "12px",
                        }}
                      >
                        ▼
                      </div>
                    </div>
                    {selectedTriggerType !== "all" && (
                      <span
                        style={{
                          color: "#a0aec0",
                          fontSize: "14px",
                        }}
                      >
                        (
                        {
                          triggers.filter(
                            (t) => t.triggerType === selectedTriggerType,
                          ).length
                        }{" "}
                        triggers)
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: "14px",
                      color:
                        triggers.length >= maxTriggers ? "#F59E0B" : "#9146FF",
                      fontWeight: "600",
                    }}
                  >
                    {triggers.length}/{maxTriggers} triggers
                  </span>
                </div>

                {triggers.filter(
                  (trigger) =>
                    selectedTriggerType === "all" ||
                    trigger.triggerType === selectedTriggerType,
                ).length > 0 ? (
                  <div style={{ display: "grid", gap: "12px" }}>
                    {triggers
                      .filter(
                        (trigger) =>
                          selectedTriggerType === "all" ||
                          trigger.triggerType === selectedTriggerType,
                      )
                      .map((trigger) => (
                        <div
                          key={trigger.id}
                          style={{
                            backgroundColor: "#2d3748",
                            padding: "16px",
                            borderRadius: "8px",
                            border: "1px solid #4a5568",
                            position: "relative",
                          }}
                        >
                          {editingTrigger === trigger.id ? (
                            // Edit form
                            <div style={{ display: "grid", gap: "16px" }}>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "16px",
                                }}
                              >
                                <div>
                                  <label
                                    style={{
                                      display: "block",
                                      marginBottom: "5px",
                                      color: "#cbd5e0",
                                      fontSize: "14px",
                                    }}
                                  >
                                    Trigger Name
                                  </label>
                                  <input
                                    type="text"
                                    value={editFormData.name}
                                    onChange={(e) =>
                                      setEditFormData({
                                        ...editFormData,
                                        name: e.target.value,
                                      })
                                    }
                                    placeholder="e.g., Greeting Response"
                                    style={{
                                      width: "100%",
                                      padding: "8px 12px",
                                      border: "1px solid #4a5568",
                                      borderRadius: "6px",
                                      backgroundColor: "#1a202c",
                                      color: "#e2e8f0",
                                      boxSizing: "border-box",
                                    }}
                                  />
                                </div>

                                <div style={{ position: "relative" }}>
                                  <label
                                    style={{
                                      display: "block",
                                      marginBottom: "5px",
                                      color: "#cbd5e0",
                                      fontSize: "14px",
                                    }}
                                  >
                                    Trigger Type
                                  </label>
                                  <select
                                    value={editFormData.triggerType}
                                    disabled={true}
                                    style={{
                                      width: "100%",
                                      padding: "8px 32px 8px 12px",
                                      border: "1px solid #4a5568",
                                      borderRadius: "6px",
                                      backgroundColor: "#2a2f3a",
                                      color: "#a0aec0",
                                      appearance: "none",
                                      WebkitAppearance: "none",
                                      MozAppearance: "none",
                                      boxSizing: "border-box",
                                      cursor: "not-allowed",
                                      opacity: 0.7,
                                    }}
                                  >
                                    <option value={editFormData.triggerType}>
                                      {
                                        triggerTypeLabels[
                                          editFormData.triggerType as keyof typeof triggerTypeLabels
                                        ]
                                      }
                                    </option>
                                  </select>
                                  <div
                                    style={{
                                      position: "absolute",
                                      right: "8px",
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      pointerEvents: "none",
                                      color: "#a0aec0",
                                      fontSize: "12px",
                                    }}
                                  >
                                    ▼
                                  </div>
                                </div>
                              </div>

                              <TriggerTypeHelp
                                triggerType={editFormData.triggerType}
                              />
                              {renderTriggerConfig(
                                editFormData,
                                setEditFormData,
                                true,
                              )}

                              {!(
                                editFormData.triggerType ===
                                  "parameterized_keyword" &&
                                editFormData.config.actionType === "webhook"
                              ) && (
                                <div>
                                  <label
                                    style={{
                                      display: "block",
                                      marginBottom: "5px",
                                      color: "#cbd5e0",
                                      fontSize: "14px",
                                    }}
                                  >
                                    Response Message
                                  </label>
                                  <textarea
                                    value={editFormData.responses.messages[0]}
                                    onChange={(e) =>
                                      setEditFormData({
                                        ...editFormData,
                                        responses: {
                                          ...editFormData.responses,
                                          messages: [e.target.value],
                                        },
                                      })
                                    }
                                    placeholder="Enter the bot's response..."
                                    style={{
                                      width: "100%",
                                      padding: "8px 12px",
                                      border: "1px solid #4a5568",
                                      borderRadius: "6px",
                                      backgroundColor: "#1a202c",
                                      color: "#e2e8f0",
                                      minHeight: "80px",
                                      resize: "vertical",
                                      boxSizing: "border-box",
                                    }}
                                  />
                                </div>
                              )}

                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: isMobile
                                    ? "1fr"
                                    : "1fr 1fr",
                                  gap: "16px",
                                }}
                              >
                                <div>
                                  <label
                                    style={{
                                      display: "block",
                                      marginBottom: "5px",
                                      color: "#cbd5e0",
                                      fontSize: "14px",
                                    }}
                                  >
                                    Cooldown (minutes)
                                  </label>
                                  <input
                                    type="number"
                                    value={
                                      editFormData.config.cooldownMinutes || 1
                                    }
                                    onChange={(e) =>
                                      setEditFormData({
                                        ...editFormData,
                                        config: {
                                          ...editFormData.config,
                                          cooldownMinutes: parseInt(
                                            e.target.value,
                                          ),
                                        },
                                      })
                                    }
                                    min="0"
                                    style={{
                                      width: "100%",
                                      padding: "8px 12px",
                                      border: "1px solid #4a5568",
                                      borderRadius: "6px",
                                      backgroundColor: "#1a202c",
                                      color: "#e2e8f0",
                                      boxSizing: "border-box",
                                    }}
                                  />
                                </div>

                                <div>
                                  <label
                                    style={{
                                      display: "block",
                                      marginBottom: "5px",
                                      color: "#cbd5e0",
                                      fontSize: "14px",
                                    }}
                                  >
                                    Max Triggers Per Hour
                                  </label>
                                  <input
                                    type="number"
                                    value={
                                      editFormData.config.maxTriggersPerHour ||
                                      10
                                    }
                                    onChange={(e) =>
                                      setEditFormData({
                                        ...editFormData,
                                        config: {
                                          ...editFormData.config,
                                          maxTriggersPerHour: parseInt(
                                            e.target.value,
                                          ),
                                        },
                                      })
                                    }
                                    min="1"
                                    style={{
                                      width: "100%",
                                      padding: "8px 12px",
                                      border: "1px solid #4a5568",
                                      borderRadius: "6px",
                                      backgroundColor: "#1a202c",
                                      color: "#e2e8f0",
                                      boxSizing: "border-box",
                                    }}
                                  />
                                </div>
                              </div>

                              <label
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#cbd5e0",
                                  fontSize: "14px",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={
                                    editFormData.responses.includeContext
                                  }
                                  onChange={(e) =>
                                    setEditFormData({
                                      ...editFormData,
                                      responses: {
                                        ...editFormData.responses,
                                        includeContext: e.target.checked,
                                      },
                                    })
                                  }
                                />
                                Use AI to generate contextual responses
                              </label>

                              <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                  onClick={() => handleSaveEdit(trigger.id)}
                                  disabled={
                                    !editFormData.name ||
                                    (!(
                                      editFormData.triggerType ===
                                        "parameterized_keyword" &&
                                      editFormData.config.actionType ===
                                        "webhook"
                                    ) &&
                                      !editFormData.responses.messages[0])
                                  }
                                  style={{
                                    padding: "10px 24px",
                                    backgroundColor:
                                      editFormData.name &&
                                      (editFormData.responses.messages[0] ||
                                        (editFormData.triggerType ===
                                          "parameterized_keyword" &&
                                          editFormData.config.actionType ===
                                            "webhook"))
                                        ? "#9146FF"
                                        : "#4a5568",
                                    color:
                                      editFormData.name &&
                                      (editFormData.responses.messages[0] ||
                                        (editFormData.triggerType ===
                                          "parameterized_keyword" &&
                                          editFormData.config.actionType ===
                                            "webhook"))
                                        ? "white"
                                        : "#718096",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor:
                                      editFormData.name &&
                                      (editFormData.responses.messages[0] ||
                                        (editFormData.triggerType ===
                                          "parameterized_keyword" &&
                                          editFormData.config.actionType ===
                                            "webhook"))
                                        ? "pointer"
                                        : "not-allowed",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                  }}
                                >
                                  Save Changes
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  style={{
                                    padding: "10px 24px",
                                    backgroundColor: "#4a5568",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            // Normal display
                            <div
                              style={{
                                display: "flex",
                                flexDirection: isMobile ? "column" : "row",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: isMobile ? "12px" : "0",
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    marginBottom: "8px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: "600",
                                      fontSize: "16px",
                                      color: "#e2e8f0",
                                    }}
                                  >
                                    {trigger.name}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      padding: "2px 8px",
                                      borderRadius: "4px",
                                      backgroundColor: "#4c1d95",
                                      color: "#ddd6fe",
                                    }}
                                  >
                                    {
                                      triggerTypeLabels[
                                        trigger.triggerType as keyof typeof triggerTypeLabels
                                      ]
                                    }
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      padding: "2px 8px",
                                      borderRadius: "4px",
                                      backgroundColor: trigger.enabled
                                        ? "#059669"
                                        : "#DC2626",
                                      color: "white",
                                    }}
                                  >
                                    {trigger.enabled ? "Enabled" : "Disabled"}
                                  </span>
                                  {trigger.metadata?.triggerCount !==
                                    undefined &&
                                    trigger.config.maxTriggersPerHour && (
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          padding: "2px 8px",
                                          borderRadius: "4px",
                                          backgroundColor: "#374151",
                                          color:
                                            trigger.metadata.triggerCount >=
                                            trigger.config.maxTriggersPerHour
                                              ? "#fbbf24"
                                              : "#9ca3af",
                                        }}
                                      >
                                        {trigger.metadata.triggerCount}/
                                        {trigger.config.maxTriggersPerHour} per
                                        hour
                                      </span>
                                    )}
                                  {getCooldownStatus(trigger) && (
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        padding: "2px 8px",
                                        borderRadius: "4px",
                                        backgroundColor: "#7c2d12",
                                        color: "#fed7aa",
                                      }}
                                    >
                                      Cooldown: {getCooldownStatus(trigger)}
                                    </span>
                                  )}
                                </div>

                                <div
                                  style={{
                                    fontSize: "14px",
                                    color: "#cbd5e0",
                                    marginBottom: "8px",
                                  }}
                                >
                                  {trigger.triggerType === "keyword" &&
                                    trigger.config.keywords && (
                                      <span>
                                        Keywords:{" "}
                                        {trigger.config.keywords.join(", ")}
                                      </span>
                                    )}
                                  {trigger.triggerType === "pattern" &&
                                    trigger.config.pattern && (
                                      <span>
                                        Pattern:{" "}
                                        <code
                                          style={{
                                            backgroundColor: "#1a202c",
                                            padding: "2px 4px",
                                            borderRadius: "3px",
                                          }}
                                        >
                                          {trigger.config.pattern}
                                        </code>
                                      </span>
                                    )}
                                  {trigger.triggerType === "silence" && (
                                    <span>
                                      Triggers after{" "}
                                      {trigger.config.silenceThresholdMinutes}{" "}
                                      minutes of silence
                                    </span>
                                  )}
                                  {trigger.triggerType ===
                                    "parameterized_keyword" && (
                                    <span>
                                      Command:{" "}
                                      <code
                                        style={{
                                          backgroundColor: "#1a202c",
                                          padding: "2px 6px",
                                          borderRadius: "3px",
                                          color: "#9146FF",
                                        }}
                                      >
                                        {trigger.config.keyword}
                                      </code>{" "}
                                      {trigger.config.actionType === "webhook"
                                        ? "(webhook)"
                                        : "(message)"}
                                      {trigger.config.allowedUsers ===
                                        "mods_only" && (
                                        <span
                                          style={{
                                            marginLeft: "8px",
                                            fontSize: "11px",
                                            padding: "2px 6px",
                                            backgroundColor: "#4c1d95",
                                            borderRadius: "3px",
                                            color: "#ddd6fe",
                                          }}
                                        >
                                          Mods Only
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </div>

                                <div
                                  style={{ fontSize: "14px", color: "#a0aec0" }}
                                >
                                  {trigger.triggerType ===
                                    "parameterized_keyword" &&
                                  trigger.config.actionType === "webhook" ? (
                                    <>
                                      Webhook:{" "}
                                      <code
                                        style={{
                                          backgroundColor: "#1a202c",
                                          padding: "2px 4px",
                                          borderRadius: "3px",
                                          fontSize: "12px",
                                        }}
                                      >
                                        {trigger.config.webhookUrl?.substring(
                                          0,
                                          50,
                                        )}
                                        {(trigger.config.webhookUrl?.length ||
                                          0) > 50
                                          ? "..."
                                          : ""}
                                      </code>
                                    </>
                                  ) : (
                                    <>
                                      Response:{" "}
                                      <em>
                                        &quot;{trigger.responses.messages[0]}
                                        &quot;
                                      </em>
                                    </>
                                  )}
                                </div>

                                {trigger.metadata?.lastTriggered && (
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: "#718096",
                                      marginTop: "8px",
                                    }}
                                  >
                                    Last triggered:{" "}
                                    {getRelativeTimeString(
                                      trigger.metadata.lastTriggered,
                                    )}
                                    {trigger.metadata.triggerCount !==
                                      undefined &&
                                      trigger.metadata.triggerCount > 0 &&
                                      ` (${trigger.metadata.triggerCount} time${
                                        trigger.metadata.triggerCount === 1
                                          ? ""
                                          : "s"
                                      } in current hour)`}
                                  </div>
                                )}
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  flexWrap: isMobile ? "wrap" : "nowrap",
                                  width: isMobile ? "100%" : "auto",
                                }}
                              >
                                <button
                                  onClick={() =>
                                    handleUpdateTrigger(trigger.id, {
                                      enabled: !trigger.enabled,
                                    })
                                  }
                                  style={{
                                    padding: "6px 12px",
                                    backgroundColor: trigger.enabled
                                      ? "#DC2626"
                                      : "#059669",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    flex: isMobile ? "1" : "0",
                                  }}
                                >
                                  {trigger.enabled ? "Disable" : "Enable"}
                                </button>
                                <button
                                  onClick={() => handleEditTrigger(trigger)}
                                  style={{
                                    padding: "6px 12px",
                                    backgroundColor: "#4a5568",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    flex: isMobile ? "1" : "0",
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteTrigger(
                                      trigger.id,
                                      trigger.name,
                                    )
                                  }
                                  style={{
                                    padding: "6px 12px",
                                    backgroundColor: "#991b1b",
                                    color: "#fecaca",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    flex: isMobile ? "1" : "0",
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundColor: "#2d3748",
                      padding: "40px",
                      borderRadius: "8px",
                      textAlign: "center",
                      color: "#a0aec0",
                    }}
                  >
                    {triggers.length === 0 ? (
                      <>
                        <p style={{ margin: 0 }}>No triggers configured yet.</p>
                        <p style={{ margin: "8px 0 0 0", fontSize: "14px" }}>
                          Create triggers to make your bot respond automatically
                          to patterns, keywords, or events.
                        </p>
                      </>
                    ) : (
                      <>
                        <p style={{ margin: 0 }}>
                          No{" "}
                          {
                            triggerTypeLabels[
                              selectedTriggerType as keyof typeof triggerTypeLabels
                            ]
                          }{" "}
                          triggers found.
                        </p>
                        <p style={{ margin: "8px 0 0 0", fontSize: "14px" }}>
                          Create a new trigger or select a different filter.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </>
            }
          />
          <Route
            path="add"
            element={
              <div
                style={{
                  backgroundColor: "#2d3748",
                  padding: "20px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  border: "1px solid #4a5568",
                }}
              >
                <h4
                  style={{
                    marginTop: 0,
                    marginBottom: "16px",
                    color: "#9146FF",
                    fontSize: "16px",
                  }}
                >
                  Create New Trigger
                </h4>

                <div style={{ display: "grid", gap: "16px" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          color: "#cbd5e0",
                          fontSize: "14px",
                        }}
                      >
                        Trigger Name
                      </label>
                      <input
                        type="text"
                        value={newTrigger.name}
                        onChange={(e) =>
                          setNewTrigger({ ...newTrigger, name: e.target.value })
                        }
                        placeholder="e.g., Greeting Response"
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #4a5568",
                          borderRadius: "6px",
                          backgroundColor: "#1a202c",
                          color: "#e2e8f0",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>

                    <div style={{ position: "relative" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          color: "#cbd5e0",
                          fontSize: "14px",
                        }}
                      >
                        Trigger Type
                      </label>
                      <select
                        value={newTrigger.triggerType}
                        onChange={(e) =>
                          setNewTrigger({
                            ...newTrigger,
                            triggerType: e.target
                              .value as BotTrigger["triggerType"],
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "8px 32px 8px 12px",
                          border: "1px solid #4a5568",
                          borderRadius: "6px",
                          backgroundColor: "#1a202c",
                          color: "#e2e8f0",
                          appearance: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                          boxSizing: "border-box",
                        }}
                      >
                        {Object.entries(triggerTypeLabels).map(
                          ([value, label]) => {
                            // Free users can only use keyword, pattern, and parameterized_keyword triggers
                            if (
                              userType === "user" &&
                              ![
                                "keyword",
                                "pattern",
                                "parameterized_keyword",
                              ].includes(value)
                            ) {
                              return null;
                            }
                            return (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            );
                          },
                        )}
                      </select>
                      <div
                        style={{
                          position: "absolute",
                          right: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          color: "#cbd5e0",
                          fontSize: "12px",
                        }}
                      >
                        ▼
                      </div>
                    </div>
                  </div>

                  {userType === "user" && (
                    <div
                      style={{
                        padding: "12px",
                        backgroundColor: "#4c1d95",
                        borderRadius: "6px",
                        fontSize: "13px",
                        color: "#e9d5ff",
                        marginBottom: "16px",
                      }}
                    >
                      <strong>
                        <Icon
                          name="star"
                          size={14}
                          style={{ verticalAlign: "-2px", marginRight: "4px" }}
                        />
                        Free Plan:
                      </strong>{" "}
                      You can use Keyword, Pattern, and Command with Parameter
                      triggers.
                      <Link
                        to="/dashboard/account"
                        style={{ color: "#ddd6fe", marginLeft: "4px" }}
                      >
                        Upgrade to Premium
                      </Link>{" "}
                      for advanced trigger types like Silence Detection,
                      Activity Spikes, Time-based, and Event triggers.
                    </div>
                  )}

                  <TriggerTypeHelp triggerType={newTrigger.triggerType} />
                  {renderTriggerConfig(newTrigger, setNewTrigger)}

                  {!(
                    newTrigger.triggerType === "parameterized_keyword" &&
                    newTrigger.config.actionType === "webhook"
                  ) && (
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          color: "#cbd5e0",
                          fontSize: "14px",
                        }}
                      >
                        Response Message
                      </label>
                      <textarea
                        value={newTrigger.responses.messages[0]}
                        onChange={(e) =>
                          setNewTrigger({
                            ...newTrigger,
                            responses: {
                              ...newTrigger.responses,
                              messages: [e.target.value],
                            },
                          })
                        }
                        placeholder="Enter the bot's response..."
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #4a5568",
                          borderRadius: "6px",
                          backgroundColor: "#1a202c",
                          color: "#e2e8f0",
                          minHeight: "80px",
                          resize: "vertical",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  )}

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          color: "#cbd5e0",
                          fontSize: "14px",
                        }}
                      >
                        Cooldown (minutes)
                      </label>
                      <input
                        type="number"
                        value={newTrigger.config.cooldownMinutes || 1}
                        onChange={(e) =>
                          setNewTrigger({
                            ...newTrigger,
                            config: {
                              ...newTrigger.config,
                              cooldownMinutes: parseInt(e.target.value),
                            },
                          })
                        }
                        min="0"
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #4a5568",
                          borderRadius: "6px",
                          backgroundColor: "#1a202c",
                          color: "#e2e8f0",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          color: "#cbd5e0",
                          fontSize: "14px",
                        }}
                      >
                        Max Triggers Per Hour
                      </label>
                      <input
                        type="number"
                        value={newTrigger.config.maxTriggersPerHour || 10}
                        onChange={(e) =>
                          setNewTrigger({
                            ...newTrigger,
                            config: {
                              ...newTrigger.config,
                              maxTriggersPerHour: parseInt(e.target.value),
                            },
                          })
                        }
                        min="1"
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #4a5568",
                          borderRadius: "6px",
                          backgroundColor: "#1a202c",
                          color: "#e2e8f0",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  </div>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "#cbd5e0",
                      fontSize: "14px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={newTrigger.responses.includeContext}
                      onChange={(e) =>
                        setNewTrigger({
                          ...newTrigger,
                          responses: {
                            ...newTrigger.responses,
                            includeContext: e.target.checked,
                          },
                        })
                      }
                    />
                    Use AI to generate contextual responses
                  </label>

                  <button
                    onClick={handleCreateTrigger}
                    disabled={
                      !newTrigger.name ||
                      (!(
                        newTrigger.triggerType === "parameterized_keyword" &&
                        newTrigger.config.actionType === "webhook"
                      ) &&
                        !newTrigger.responses.messages[0])
                    }
                    style={{
                      padding: "10px 24px",
                      backgroundColor:
                        newTrigger.name &&
                        (newTrigger.responses.messages[0] ||
                          (newTrigger.triggerType === "parameterized_keyword" &&
                            newTrigger.config.actionType === "webhook"))
                          ? "#9146FF"
                          : "#4a5568",
                      color:
                        newTrigger.name &&
                        (newTrigger.responses.messages[0] ||
                          (newTrigger.triggerType === "parameterized_keyword" &&
                            newTrigger.config.actionType === "webhook"))
                          ? "white"
                          : "#718096",
                      border: "none",
                      borderRadius: "6px",
                      cursor:
                        newTrigger.name &&
                        (newTrigger.responses.messages[0] ||
                          (newTrigger.triggerType === "parameterized_keyword" &&
                            newTrigger.config.actionType === "webhook"))
                          ? "pointer"
                          : "not-allowed",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Create Trigger
                  </button>
                </div>
              </div>
            }
          />
        </Routes>
      </div>

      {/* Triggers Info Panel */}
      <div
        style={{
          width: isMobile ? "100%" : "350px",
          maxWidth: isMobile ? "800px" : "350px",
          backgroundColor: "rgba(45, 55, 72, 0.95)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          borderRadius: "8px",
          padding: "20px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
          height: "fit-content",
          position: isMobile ? "static" : "sticky",
          top: "100px",
          order: isMobile ? 2 : 0,
          boxSizing: "border-box",
        }}
      >
        <h3
          style={{
            color: "#e2e8f0",
            marginTop: 0,
            marginBottom: "20px",
            fontSize: "18px",
          }}
        >
          <Icon
            name="zap"
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Triggers Guide
        </h3>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="target"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            What are Triggers?
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            Automated responses that activate when specific conditions are met
            in chat, without needing to mention HAL directly.
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="clipboard-list"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            Trigger Types
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>
              <strong>Keyword</strong> - Responds to specific words
            </li>
            <li>
              <strong>Pattern</strong> - Uses regex for complex matching
            </li>
            <li>
              <strong>Command</strong> - Custom commands with parameters
            </li>
          </ul>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="star"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            Premium Trigger Types
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>
              <strong>Silence</strong> - Activates after chat quiet time
            </li>
            <li>
              <strong>Activity Spike</strong> - Triggers when chat gets busy
            </li>
            <li>
              <strong>Time-based</strong> - Runs on schedule (cron)
            </li>
            <li>
              <strong>User Join</strong> - Welcomes new viewers
            </li>
            <li>
              <strong>Raid</strong> - Thanks incoming raids
            </li>
            <li>
              <strong>Follow</strong> - Welcomes new followers
            </li>
            <li>
              <strong>Subscribe</strong> - Celebrates new subs
            </li>
          </ul>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="settings"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            Configuration Options
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>
              <strong>Cooldown</strong> - Time between triggers
            </li>
            <li>
              <strong>Max/Hour</strong> - Rate limiting
            </li>
            <li>
              <strong>Case Sensitive</strong> - Exact matching
            </li>
            <li>
              <strong>Context Mode</strong> - AI-powered responses
            </li>
          </ul>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="lightbulb"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            Best Practices
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>Use cooldowns to prevent spam</li>
            <li>Test patterns before enabling</li>
            <li>Keep responses relevant & fun</li>
            <li>Monitor trigger statistics</li>
          </ul>
        </div>

        <div
          style={{
            backgroundColor: "#1a202c",
            padding: "12px",
            borderRadius: "6px",
            marginTop: "15px",
            border: "1px solid #4a5568",
          }}
        >
          <p
            style={{
              color: "#9146FF",
              fontSize: "12px",
              margin: "0 0 6px 0",
              fontWeight: "bold",
            }}
          >
            {isPremium ? (
              <>
                <Icon
                  name="star"
                  size={12}
                  style={{ verticalAlign: "-2px", marginRight: "4px" }}
                />
                Premium User
              </>
            ) : (
              "Free Plan"
            )}
          </p>
          <p style={{ color: "#a0aec0", fontSize: "11px", margin: 0 }}>
            {isPremium
              ? `You can create up to ${maxTriggers} triggers. Currently using ${triggers.length}.`
              : `Free users can create up to ${maxTriggers} triggers. Upgrade to Premium for up to 100 triggers!`}
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#1a202c",
            padding: "12px",
            borderRadius: "6px",
            marginTop: "15px",
            border: "1px solid #4a5568",
          }}
        >
          <p
            style={{
              color: "#9146FF",
              fontSize: "12px",
              margin: "0 0 6px 0",
              fontWeight: "bold",
            }}
          >
            <Icon
              name="rocket"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "4px" }}
            />
            Quick Examples:
          </p>
          <ul
            style={{
              color: "#a0aec0",
              fontSize: "11px",
              margin: 0,
              paddingLeft: "16px",
            }}
          >
            <li>
              <strong>Keyword:</strong> !commands{" "}
              <Icon
                name="arrow-right"
                size={12}
                style={{ verticalAlign: "-2px", margin: "0 2px" }}
              />{" "}
              List available commands
            </li>
            <li>
              <strong>Pattern:</strong> gg|wp|gj{" "}
              <Icon
                name="arrow-right"
                size={12}
                style={{ verticalAlign: "-2px", margin: "0 2px" }}
              />{" "}
              &quot;Nice play!&quot;
            </li>
            <li>
              <strong>Command:</strong> !so user{" "}
              <Icon
                name="arrow-right"
                size={12}
                style={{ verticalAlign: "-2px", margin: "0 2px" }}
              />{" "}
              &quot;Check out user!&quot;
            </li>
            <li>
              <strong>Silence:</strong> 5 min quiet{" "}
              <Icon
                name="arrow-right"
                size={12}
                style={{ verticalAlign: "-2px", margin: "0 2px" }}
              />{" "}
              &quot;Chat&apos;s quiet, let&apos;s talk!&quot;
            </li>
            <li>
              <strong>Activity:</strong> 50+ msgs/min{" "}
              <Icon
                name="arrow-right"
                size={12}
                style={{ verticalAlign: "-2px", margin: "0 2px" }}
              />{" "}
              &quot;Wow, chat&apos;s popping off!&quot;
            </li>
            <li>
              <strong>Time:</strong> Every hour{" "}
              <Icon
                name="arrow-right"
                size={12}
                style={{ verticalAlign: "-2px", margin: "0 2px" }}
              />{" "}
              &quot;Reminder: Stay hydrated!&quot;
            </li>
            <li>
              <strong>Follow:</strong> New follower{" "}
              <Icon
                name="arrow-right"
                size={12}
                style={{ verticalAlign: "-2px", margin: "0 2px" }}
              />{" "}
              Welcome message
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TriggerManager;
